import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  accordionSummaryStyles,
  cardStyles,
  DragAndDropStack,
  MemberStaffCard,
  useGlobalMessageHandler,
  useUsLegRoleTypeLuts,
  PersonSelectDialog
} from 'components'
import { BulkAssignButton } from 'components/us/bulk-assign/bulk-assign-button'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Fab,
  Stack,
  Typography
} from '@mui/material'
import { useSessionContext } from 'session'
import {
  type CommitteeBasicInformation,
  getComBasicInfo,
  type PersonBaseBasicInformation,
  updateComStafferDisplayOrder,
  getComAddress
} from 'services'
import { useParams } from 'react-router-dom'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import { getEmptyPersonAddress, getEmptyStafferEmail, getEmptyPersonPhone } from 'helpers'
import { bulkAssignCommittee, type CommitteeStaffInformation, getStaffByCommittee } from 'services/commitee/committeeStaffService'
import { MajMinAffilication, US_COMMITTEE_ROLES, US_STAFFER_ROLES, PHONE_TYPES } from 'utils'

const LoadingSpinner: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
    <CircularProgress />
  </Box>
)

export const CommitteeStaffersPage: React.FC = () => {
  const { comId } = useParams<{ comId: string }>()
  const parsedComId = comId != null ? parseInt(comId, 10) : 0
  const { changeModuleName, changeUniqueId } = useSessionContext()
  const [loading, setLoading] = useState(true)
  const { showErrorMessage } = useGlobalMessageHandler()
  const [expanded, setExpanded] = useState(true)
  const [expandedMin, setExpandedMin] = useState(true)
  const [expandedOth, setExpandedOth] = useState(true)
  const [committee, setCommittee] = useState<CommitteeBasicInformation | undefined>()

  const [reloadTrigger, setReloadTrigger] = useState(0)
  const [openStaffDialog, setOpenStaffDialog] = useState(false)
  const [createMajMin, setCreateMajMin] = useState(MajMinAffilication.MAJ)

  const [majStaff, setMajMajStaff] = useState<CommitteeStaffInformation[]>([])
  const [minStaff, setMinStaff] = useState<CommitteeStaffInformation[]>([])
  const [othStaff, setOthStaff] = useState<CommitteeStaffInformation[]>([])
  const [committeeAddresses, setCommitteeAddresses] = useState<any[]>([])

  const [creatingStaff, setCreatingStaff] = useState<CommitteeStaffInformation | undefined>(undefined)
  const { lutValues: legRoleTypeLutValues, loading: loadingLegRoleTypeLutValues } = useUsLegRoleTypeLuts()

  useEffect(() => {
    changeModuleName('Committee Staffers')
  }, [changeModuleName])

  // Add this new useEffect to set the document title and uniqueId when committee data is loaded
  useEffect(() => {
    if (committee?.id != null) {
      changeUniqueId(`${parsedComId} - ${committee.name}`)
      document.title = `Staffers of Com ${parsedComId} - ${committee.name}`
    } else {
      changeUniqueId(parsedComId.toString())
    }
  }, [committee, parsedComId, changeUniqueId])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true)
        const comStaffInfoResponse = await getStaffByCommittee(parsedComId)
        const committee = await getComBasicInfo(parsedComId)
        const addressResponse = await getComAddress(parsedComId)
        setCommittee(committee)
        setCommitteeAddresses(addressResponse.addresses)
        setMajMajStaff(comStaffInfoResponse.filter(staff => staff.majMin === 'MAJ'))
        setMinStaff(comStaffInfoResponse.filter(staff => staff.majMin === 'MIN'))
        setOthStaff(comStaffInfoResponse.filter(staff => staff.majMin === 'OTH'))
      } catch (error: any) {
        let errorMessage = 'An unexpected error occurred during the request.'
        if (error.response?.status === 404 || error.response?.status === 400) {
          errorMessage = Object.values(error.response.data.errors).join('\n')
        }
        showErrorMessage(errorMessage)
      } finally {
        setLoading(false)
      }
    }
    void fetchData()
  }, [parsedComId])

  const addStaffer = (majmin: MajMinAffilication): void => {
    setCreateMajMin(majmin)
    setOpenStaffDialog(true)
  }

  const refreshStaff = useCallback(async (): Promise<void> => {
    try {
      const refreshed = await getStaffByCommittee(parsedComId)
      setMajMajStaff(refreshed.filter(staff => staff.majMin === 'MAJ'))
      setMinStaff(refreshed.filter(staff => staff.majMin === 'MIN'))
      setOthStaff(refreshed.filter(staff => staff.majMin === 'OTH'))
      setReloadTrigger(prev => prev + 1)
    } catch (error: any) {
      showErrorMessage('Failed to refresh staffers after bulk-assign.')
    }
  }, [parsedComId, showErrorMessage])

  const handleBulkAssignSuccess = useCallback(async (): Promise<void> => {
    await refreshStaff()
  }, [refreshStaff])

  const hasMinorityAddress = useMemo(
    () => committeeAddresses.some((address: any) => address.displayOrder === 2),
    [committeeAddresses]
  )

  const majoritySourceAddress = useMemo(
    () => committeeAddresses.find((address: any) => address.displayOrder === 1),
    [committeeAddresses]
  )

  const minoritySourceAddress = useMemo(
    () => committeeAddresses.find((address: any) => address.displayOrder === 2),
    [committeeAddresses]
  )

  const ownerDisplayName = committee?.name ?? 'this committee'

  const getComStafferRole = (committeeRoleId: number): number => {
    switch (committeeRoleId) {
      case US_COMMITTEE_ROLES.FED_COMMITTEE:
      case US_COMMITTEE_ROLES.FED_SUBCOMMITTEE:
        return US_STAFFER_ROLES.FEDERAL_COMMITTE_STAFF
      case US_COMMITTEE_ROLES.FED_CAUCUS:
        return US_STAFFER_ROLES.FEDERAL_CAUCUS_STAFF
      case US_COMMITTEE_ROLES.STATE_COMMITTEE:
      case US_COMMITTEE_ROLES.STATE_SUBCOMMITTEE:
        return US_STAFFER_ROLES.STATE_COMMITTE_STAFF
      case US_COMMITTEE_ROLES.STATE_CAUCUS:
        return US_STAFFER_ROLES.STATE_CAUCUS_STAFF
      default:
        return US_STAFFER_ROLES.FEDERAL_COMMITTE_STAFF
    }
  }

  const handlePersonSelected = (person: PersonBaseBasicInformation): void => {
    if (committee == null) return
    setOpenStaffDialog(false)

    const emptyAddress = getEmptyPersonAddress(person.id ?? 0)

    const defaultPhone = getEmptyPersonPhone(person.id ?? 0, emptyAddress.id)
    defaultPhone.typeId = PHONE_TYPES.PHONE

    const defaultFax = getEmptyPersonPhone(person.id ?? 0, emptyAddress.id)
    defaultFax.typeId = PHONE_TYPES.FAX

    emptyAddress.phones = [defaultPhone, defaultFax]

    const newStaffer: CommitteeStaffInformation = {
      id: 0,
      person,
      role: { id: getComStafferRole(committee.role.id), desc: '', code: '', branch: { id: committee.role.branch.id, branch_desc: '' } },
      legrole: undefined,
      legrole2: undefined,
      legrole3: undefined,
      committee,
      majMin: createMajMin.substring(0, 3).toUpperCase() as keyof typeof MajMinAffilication,
      statusId: undefined,
      title: undefined,
      email: getEmptyStafferEmail(person.id ?? 0),
      address: emptyAddress,
      displayOrder: 0,
      updatedBy: undefined,
      lastUpdated: undefined,
      responsibilities: []
    }
    setCreatingStaff(newStaffer)
  }

  const handleAccordionChange = (event: React.ChangeEvent<any>, isExpanded: boolean): void => {
    if ((event.target as HTMLElement).closest('.MuiSwitch-root') == null) {
      setExpanded(isExpanded)
    }
  }

  const handleAccordionChangeMin = (event: React.ChangeEvent<any>, isExpanded: boolean): void => {
    if ((event.target as HTMLElement).closest('.MuiSwitch-root') == null) {
      setExpandedMin(isExpanded)
    }
  }

  const handleAccordionChangeOth = (event: React.ChangeEvent<any>, isExpanded: boolean): void => {
    if ((event.target as HTMLElement).closest('.MuiSwitch-root') == null) {
      setExpandedOth(isExpanded)
    }
  }

  const handleStaffRetire = (retiredStaff: CommitteeStaffInformation, majMin: MajMinAffilication): void => {
    if (majMin === MajMinAffilication.MAJ) setMajMajStaff(prevStaff => prevStaff.filter(staff => staff.id !== retiredStaff.id))
    if (majMin === MajMinAffilication.MIN) setMinStaff(prevStaff => prevStaff.filter(staff => staff.id !== retiredStaff.id))
    if (majMin === MajMinAffilication.OTH) setOthStaff(prevStaff => prevStaff.filter(staff => staff.id !== retiredStaff.id))

    setReloadTrigger(prev => prev + 1)
  }

  const renderStafferCardsByType = (staffers: CommitteeStaffInformation[], majMin: MajMinAffilication, setStaff: React.Dispatch<React.SetStateAction<CommitteeStaffInformation[]>>): React.ReactNode => {
    if (staffers == null) return null

    if (staffers.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60px', color: 'text.secondary' }}>
          <Typography variant="body2">No staffers found</Typography>
        </Box>
      )
    }

    const sortedStaff = staffers.sort((a, b) => {
      const displayOrderA = a.displayOrder ?? 0
      const displayOrderB = b.displayOrder ?? 0
      return displayOrderA - displayOrderB
    })

    const staffersByRoleMap = sortedStaff.reduce((map, staffer) => {
      const roleId = staffer.legrole?.id ?? 0
      if (map.get(roleId) == null) {
        map.set(roleId, [])
      }
      map.get(roleId)?.push(staffer)
      return map
    }, new Map<number, CommitteeStaffInformation[]>())

    const sortedStaffersByRole = Array.from(staffersByRoleMap.entries())
      .sort(([_, staffersA], [__, staffersB]) => {
        const lutOrderA = staffersA[0]?.legrole?.lutOrder ?? 0
        const lutOrderB = staffersB[0]?.legrole?.lutOrder ?? 0
        return lutOrderA - lutOrderB
      })

    const groupedStaffList = (
      <Stack spacing={2}>
        {sortedStaffersByRole.flatMap(([roleId, staffers]) => (
          <DragAndDropStack
            key={roleId}
            id={`${majMin}-staff-${parsedComId ?? ''}-${roleId}`}
            originalItems={staffers}
            itemRenderer={(item) => (
              <MemberStaffCard
                key={item.id}
                createCard={false}
                originalPosition={item}
                legRoleLuts={legRoleTypeLutValues}
                loadingLegRoleTypeLutValues={loadingLegRoleTypeLutValues}
                onStaffUpdate={(updatedStaff) => { handleStaffUpdate(updatedStaff, majMin) }}
                onRetire={(retiredStaff) => { handleStaffRetire(retiredStaff, majMin) }}
                committeeAddresses={committeeAddresses}
              />
            )}
            updateDisplayOrder={async (items, itemSelected) => {
              const displayOrderById = items.reduce<Record<number, number>>((acc, staff, index) => ({ ...acc, [staff.id]: index + 1 }), {})
              const response = await updateComStafferDisplayOrder(parsedComId, displayOrderById, itemSelected.id, itemSelected.legrole.id, itemSelected.majMin.toUpperCase() as keyof typeof MajMinAffilication)
              if (response.succeeded) {
                setStaff(prevStaff =>
                  [...prevStaff.map(staff =>
                    displayOrderById[staff.id] != null ? { ...staff, displayOrder: displayOrderById[staff.id] } : staff
                  )]
                )
              }
              return response
            }}
            disableDrag={loading || staffers.length <= 1}
            makeGray={false}
          />
        ))}
      </Stack>
    )

    return groupedStaffList
  }

  const handleStaffUpdate = (updatedStaff: CommitteeStaffInformation, majMin: MajMinAffilication): void => {
    if (majMin === MajMinAffilication.MAJ) setMajMajStaff(prevStaff => prevStaff.map(staff => staff.id === updatedStaff.id ? updatedStaff : staff))
    if (majMin === MajMinAffilication.MIN) setMinStaff(prevStaff => prevStaff.map(staff => staff.id === updatedStaff.id ? updatedStaff : staff))
    if (majMin === MajMinAffilication.OTH) setOthStaff(prevStaff => prevStaff.map(staff => staff.id === updatedStaff.id ? updatedStaff : staff))

    setReloadTrigger(prev => prev + 1)
  }

  const memoizedMajorityStafferCardsByType = React.useMemo(
    () => renderStafferCardsByType(majStaff, MajMinAffilication.MAJ, setMajMajStaff),
    [majStaff, comId, reloadTrigger]
  )

  const memoizedMinorityStafferCardsByType = React.useMemo(
    () => renderStafferCardsByType(minStaff, MajMinAffilication.MIN, setMinStaff),
    [minStaff, comId, reloadTrigger]
  )

  const memoizedOtherStafferCardsByType = React.useMemo(
    () => renderStafferCardsByType(othStaff, MajMinAffilication.OTH, setOthStaff),
    [minStaff, comId, reloadTrigger]
  )

  const addToExistingStaffers = (newStaffer: CommitteeStaffInformation, majMin: MajMinAffilication): void => {
    if (majMin === MajMinAffilication.MAJ) setMajMajStaff(prevStaff => [...prevStaff, newStaffer])
    if (majMin === MajMinAffilication.MIN) setMinStaff(prevStaff => [...prevStaff, newStaffer])
    if (majMin === MajMinAffilication.OTH) setOthStaff(prevStaff => [...prevStaff, newStaffer])

    setReloadTrigger(prev => prev + 1)
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 1
        }}
      >
        <Stack spacing={2} alignItems="center" mt={1} width="98%">
          <Card sx={cardStyles}>
            <Accordion expanded={expanded} onChange={handleAccordionChange}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header" sx={accordionSummaryStyles}>
                  <Typography variant="h6">Majority Staffers</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CardContent>
                  {loading
                    ? <LoadingSpinner />
                    : (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                        <BulkAssignButton
                          ownerDisplayName={ownerDisplayName}
                          targetLabel={hasMinorityAddress ? 'Majority office' : 'office'}
                          buttonLabel={hasMinorityAddress
                            ? 'Apply Majority contact info to MAJ + OTH staffers'
                            : 'Apply committee contact info to all staffers'}
                          dialogTitle={hasMinorityAddress
                            ? 'Bulk-assign Majority contact info'
                            : 'Bulk-assign committee contact info'}
                          staffers={hasMinorityAddress
                            ? [...majStaff, ...othStaff]
                            : [...majStaff, ...minStaff, ...othStaff]}
                          hasSourceAddress={majoritySourceAddress?.id != null}
                          onConfirm={async () => await bulkAssignCommittee(parsedComId, 1)}
                          onSuccess={handleBulkAssignSuccess}
                        />
                      </Box>
                      {creatingStaff == null &&
                          <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
                            <Fab color="primary" aria-label="add" onClick={() => { addStaffer(MajMinAffilication.MAJ) } }>
                              <AddIcon />
                            </Fab>
                          </Box>
                      }
                      {creatingStaff != null && createMajMin === MajMinAffilication.MAJ && (
                          <Box mt={2}>
                            <MemberStaffCard
                                key="new-staff-card"
                                createCard={true}
                                originalPosition={creatingStaff}
                                legRoleLuts={legRoleTypeLutValues}
                                loadingLegRoleTypeLutValues={loadingLegRoleTypeLutValues}
                                onStaffUpdate={setCreatingStaff}
                                cancelCreate={() => { setCreatingStaff(undefined) }}
                                addToExistingStaffers={(createdStaff) => { addToExistingStaffers(createdStaff, MajMinAffilication.MAJ) }}
                                committeeAddresses={committeeAddresses}
                            />
                          </Box>
                      )}
                      {memoizedMajorityStafferCardsByType}
                    </>
                      )}
                </CardContent>
              </AccordionDetails>
            </Accordion>
          </Card>
          <Card sx={cardStyles}>
            <Accordion expanded={expandedMin} onChange={handleAccordionChangeMin}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header" sx={accordionSummaryStyles}>
                <Typography variant="h6">Minority Staffers</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CardContent>
                  {loading
                    ? <LoadingSpinner />
                    : (
                    <>
                      {hasMinorityAddress && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                          <BulkAssignButton
                            ownerDisplayName={ownerDisplayName}
                            targetLabel="Minority office"
                            buttonLabel="Apply Minority contact info to MIN staffers"
                            dialogTitle="Bulk-assign Minority contact info"
                            staffers={minStaff}
                            hasSourceAddress={minoritySourceAddress?.id != null}
                            onConfirm={async () => await bulkAssignCommittee(parsedComId, 2)}
                            onSuccess={handleBulkAssignSuccess}
                          />
                        </Box>
                      )}
                      {creatingStaff == null &&
                          <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
                            <Fab color="primary" aria-label="add" onClick={() => { addStaffer(MajMinAffilication.MIN) } }>
                              <AddIcon />
                            </Fab>
                          </Box>
                      }
                      {creatingStaff != null && createMajMin === MajMinAffilication.MIN && (
                          <Box mt={2}>
                            <MemberStaffCard
                                key="new-min-staff-card"
                                createCard={true}
                                originalPosition={creatingStaff}
                                legRoleLuts={legRoleTypeLutValues}
                                loadingLegRoleTypeLutValues={loadingLegRoleTypeLutValues}
                                onStaffUpdate={setCreatingStaff}
                                cancelCreate={() => { setCreatingStaff(undefined) }}
                                addToExistingStaffers={(createdStaff) => { addToExistingStaffers(createdStaff, MajMinAffilication.MIN) }}
                                committeeAddresses={committeeAddresses}
                            />
                          </Box>
                      )}
                      {memoizedMinorityStafferCardsByType}
                    </>
                      )}
                </CardContent>
              </AccordionDetails>
            </Accordion>
          </Card>
          <Card sx={cardStyles}>
            <Accordion expanded={expandedOth} onChange={handleAccordionChangeOth}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header" sx={accordionSummaryStyles}>
                <Typography variant="h6">Other Staffers</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CardContent>
                  {loading
                    ? <LoadingSpinner />
                    : (
                    <>
                      {creatingStaff == null &&
                          <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
                            <Fab color="primary" aria-label="add" onClick={() => { addStaffer(MajMinAffilication.OTH) } }>
                              <AddIcon />
                            </Fab>
                          </Box>
                      }
                      {creatingStaff != null && createMajMin === MajMinAffilication.OTH && (
                          <Box mt={2}>
                            <MemberStaffCard
                                key="new-oth-staff-card"
                                createCard={true}
                                originalPosition={creatingStaff}
                                legRoleLuts={legRoleTypeLutValues}
                                loadingLegRoleTypeLutValues={loadingLegRoleTypeLutValues}
                                onStaffUpdate={setCreatingStaff}
                                cancelCreate={() => { setCreatingStaff(undefined) }}
                                addToExistingStaffers={(createdStaff) => { addToExistingStaffers(createdStaff, MajMinAffilication.OTH) }}
                                committeeAddresses={committeeAddresses}
                            />
                          </Box>
                      )}
                      {memoizedOtherStafferCardsByType}
                    </>
                      )}
                </CardContent>
              </AccordionDetails>
            </Accordion>
          </Card>
        </Stack>
      </Box>
      <PersonSelectDialog<PersonBaseBasicInformation>
        open={openStaffDialog}
        onClose={() => { setOpenStaffDialog(false) }}
        onPersonSelected={handlePersonSelected}
        title="Select Person"
        personType="personUS"
        fullWidth={true}
        maxWidth="md"
        minHeight="60vh"
      />
    </>
  )
}
